import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Popover,
  IconButton,
  Button
} from '@mui/material';
import { Palette as PaletteIcon } from '@mui/icons-material';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

const predefinedColors = [
  '#1f93ff', '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#d946ef', '#f43f5e', '#ef4444', '#f97316', '#eab308'
];

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [customColor, setCustomColor] = useState(color);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (selectedColor: string) => {
    onChange(selectedColor);
    setCustomColor(selectedColor);
    handleClose();
  };

  const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value;
    setCustomColor(newColor);
  };

  const handleCustomColorApply = () => {
    onChange(customColor);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          component="button"
          sx={{
            width: 40,
            height: 40,
            backgroundColor: color,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            cursor: 'pointer',
            padding: 0,
            outline: 'none',
            '&:hover': {
              opacity: 0.8
            }
          }}
          onClick={handleClick}
        />
        <IconButton onClick={handleClick} size="small">
          <PaletteIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          {color}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Choose a color
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {predefinedColors.map((preColor) => (
              <Grid item key={preColor}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: preColor,
                    border: color === preColor ? '2px solid #000' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                  onClick={() => handleColorSelect(preColor)}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" gutterBottom>
            Custom color
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              value={customColor}
              onChange={handleCustomColorChange}
              placeholder="#1f93ff"
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: customColor,
                      border: '1px solid #e0e0e0',
                      borderRadius: 0.5,
                      mr: 1
                    }}
                  />
                )
              }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleCustomColorApply}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};